import React, { useEffect } from 'react';
import { useWindowDimensions } from 'react-native';
import {
  HStack, Box, Image, useToken,
} from '@gluestack-ui/themed';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { createStackNavigator } from '@react-navigation/stack';
import { NavigationContainerRefWithCurrent, NavigationProp } from '@react-navigation/native';

import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import { GenericPanel } from '@/admin/Panels/GenericPanel';
import GradingStatsPanel from '@/admin/Panels/GradingStatsPanel';
import AiTutorDashboard from '@/admin/dashboards/aiTutor/AiTutorDashboard';
import FormNavigator from '@/admin/Panels/Forms/FormNavigator';
import AdminAssistant from '@/admin/assistant/AdminAssistant';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import useDeepLinkHandler from './useDeepLinkHandler';

import AdminHeaderLeft from './Admin/AdminHeaderLeft';
import AdminHeaderRight from './Admin/AdminHeaderRight';
import AdminDrawerContent from './Admin/AdminDrawerContent';
import ResolutionGuard from '../components/ResolutionGuard';

const Drawer = createDrawerNavigator();
const Stack = createStackNavigator();

const DrawerNavigator: React.FC = () => {
  const white = useToken('colors', 'white');

  return (
    <Drawer.Navigator
      id="adminDrawer"
      drawerContent={(props) => <AdminDrawerContent {...props} />}
      screenOptions={{
        drawerPosition: 'left',
        drawerType: 'permanent',
        headerShown: false,
        drawerStyle: {
          width: 200,
          backgroundColor: white,
          borderRightWidth: 0,
        },
      }}
    >
      <Drawer.Screen
        name="Users"
        component={GenericPanel}
        options={{ title: 'Users' }}
        initialParams={{ title: 'Users', objectName: 'users' }}
      />
      <Drawer.Screen
        name="Courses"
        component={GenericPanel}
        options={{ title: 'Courses' }}
        initialParams={{ title: 'Courses', objectName: 'courses' }}
      />
      <Drawer.Screen
        name="Cohorts"
        component={GenericPanel}
        options={{ title: 'Cohorts' }}
        initialParams={{ title: 'Cohorts', objectName: 'cohorts' }}
      />
      <Drawer.Screen
        name="Modules"
        component={GenericPanel}
        options={{ title: 'Modules' }}
        initialParams={{ title: 'Modules', objectName: 'modules' }}
      />
      <Drawer.Screen
        name="Approvals"
        component={GenericPanel}
        options={{ title: 'Approvals' }}
        initialParams={{
          title: 'Module Approvals',
          objectName: 'modules/approvals',
          filter: { state: 'pending_approval' },
        }}
      />
      <Drawer.Screen
        name="Submissions"
        component={GenericPanel}
        options={{ title: 'Submissions' }}
        initialParams={{
          title: 'Submissions Report',
          objectName: 'reports/submissions',
        }}
      />
      <Drawer.Screen
        name="GradingStats"
        component={GradingStatsPanel}
        options={{ title: 'Grading Statistics' }}
      />
      <Drawer.Screen
        name="AiTutor"
        component={AiTutorDashboard}
        options={{ title: 'AI Tutor' }}
      />
    </Drawer.Navigator>
  );
};

interface AdminNavigationProps {
  navigation?: NavigationProp<Record<string, object | undefined>>;
  navigationRef?: NavigationContainerRefWithCurrent<Record<string, object | undefined>>;
}

const AdminNavigation: React.FC<AdminNavigationProps> = ({ navigation, navigationRef }) => {
  const { width, height } = useWindowDimensions();
  const settings = useTenantSettingsStore((state) => state.settings);
  const imageAssetUrls = settings?.imageAssetUrls;
  const { isFeatureActive } = useUserFeatures();
  const isAssistantActive = isFeatureActive('admin-agent');

  const white = useToken('colors', 'white');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const borderLight = useToken('colors', 'borderLight' as any);

  useDeepLinkHandler({ navigationRef });

  // Minimum resolution check
  const isResolutionMet = width >= 1024 && height >= 500;

  useEffect(() => {
    if (navigation) {
      navigation.setOptions({
        headerTitle: '',
      });
    }
  }, [navigation]);

  if (!isResolutionMet) {
    return (
      <ResolutionGuard
        width={width}
        height={height}
        headerLeft={<AdminHeaderLeft />}
        headerRight={<AdminHeaderRight />}
      />
    );
  }

  return (
    <HStack w="100%" h="100%" p={0} m={0} bg="$bgApp">
      {!!imageAssetUrls?.logoWatermarkUrl && (
        <Image
          source={{
            uri: imageAssetUrls.logoWatermarkUrl,
            cache: 'force-cache',
          }}
          alt="Watermark"
          position="absolute"
          bottom={0}
          right={0}
          w={250}
          h={500}
          zIndex={0}
          opacity={0.15}
        />
      )}
      <Box width="100%" height="100%" position="relative">
        <Stack.Navigator
          id="adminNavigation"
          initialRouteName="Main"
          screenOptions={{
            headerShown: true,
            headerTitle: '',
            headerLeft: () => <AdminHeaderLeft />,
            headerRight: () => <AdminHeaderRight />,
            headerStyle: {
              backgroundColor: white,
              borderBottomWidth: 1,
              borderBottomColor: borderLight,
              elevation: 0,
              shadowOpacity: 0,
            },
            headerTitleAlign: 'left',
          }}
        >
          <Stack.Screen name="Main" component={DrawerNavigator} />
          <Stack.Screen
            name="CreateEditForm"
            component={FormNavigator}
            options={({ route }: { route: { params?: { title?: string } } }) => ({
              title: route.params?.title || 'Form',
            })}
          />
        </Stack.Navigator>
        {!!isAssistantActive && <AdminAssistant />}
      </Box>
    </HStack>
  );
};

export default AdminNavigation;
